import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Table, Badge, Alert, ProgressBar, Form, Modal, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import config from '../../config';
import { toast } from 'react-toastify';
import { FaMoneyBillWave, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa';
import { useHasPermission, PERMISSIONS } from '../../utils/permissions';

export default function SOPaymentTab({ orderId, orderData, onRefresh }) {
  const [payments, setPayments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentMethod: 'Bank Transfer',
    transactionReference: '',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // const canViewPayment = useHasPermission(PERMISSIONS.SO_PAYMENT_VIEW);
  const canAddPayment = useHasPermission(PERMISSIONS.SO_PAYMENT_ADD);
  // const canEditPayment = useHasPermission(PERMISSIONS.SO_PAYMENT_EDIT);


  const fetchPayments = useCallback(async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/so-payments/${orderId}`);
      setPayments(res.data || []);
    } catch (err) {
      console.error('Error fetching payments:', err);
    }
  },[orderId]);

  useEffect(() => {
    if (orderId) {
      fetchPayments();
    }
  }, [orderId, fetchPayments]);

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      await axios.post(`${config.API_BASE_URL}/so-payments`, {
        salesOrderId: orderId,
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      });
      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      resetPaymentForm();

      // Refresh payments and trigger parent refresh to update orderData
      await fetchPayments();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error recording payment');
    }
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      amount: '',
      paymentMethod: 'Bank Transfer',
      transactionReference: '',
      paymentDate: new Date().toISOString().split('T')[0],
      notes: ''
    });
  };

  const totalPaid = payments
    .filter(p => p.status === 'Received')
    .reduce((sum, p) => sum + p.amount, 0);

  const pendingPayments = payments
    .filter(p => p.status === 'Pending')
    .reduce((sum, p) => sum + p.amount, 0);

  const orderTotal = orderData?.totalAmount || 0;
  const remainingBalance = orderTotal - totalPaid;
  const paymentPercentage = orderTotal > 0 ? (totalPaid / orderTotal) * 100 : 0;

  const getPaymentStatusBadge = (status) => {
    const map = {
      'Pending': 'warning',
      'Confirmed': 'success',
      'Cancelled': 'danger'
    };
    return <Badge bg={map[status] || 'secondary'}>{status}</Badge>;
  };

  const getPaymentMethodIcon = (method) => {
    return <FaMoneyBillWave className="me-2" />;
  };

  return (
    <div>
      {/* Payment Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <FaMoneyBillWave className="text-primary fs-3 me-3" />
                <div>
                  <small className="text-muted">Total Order Value</small>
                  <h5 className="mb-0">₹{orderTotal.toLocaleString()}</h5>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <FaCheckCircle className="text-success fs-3 me-3" />
                <div>
                  <small className="text-muted">Amount Paid</small>
                  <h5 className="mb-0 text-success">₹{totalPaid.toLocaleString()}</h5>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <FaHourglassHalf className="text-warning fs-3 me-3" />
                <div>
                  <small className="text-muted">Pending</small>
                  <h5 className="mb-0 text-warning">₹{pendingPayments.toLocaleString()}</h5>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <FaMoneyBillWave className="text-danger fs-3 me-3" />
                <div>
                  <small className="text-muted">Balance Due</small>
                  <h5 className="mb-0 text-danger">₹{remainingBalance.toLocaleString()}</h5>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Payment Progress */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Payment Progress</h6>
            <span className="text-muted">{paymentPercentage.toFixed(1)}%</span>
          </div>
          <ProgressBar
            now={paymentPercentage}
            variant={paymentPercentage === 100 ? 'success' : 'primary'}
            style={{ height: '25px' }}
            label={`₹${totalPaid.toLocaleString()} / ₹${orderTotal.toLocaleString()}`}
          />
        </Card.Body>
      </Card>

      {/* Action Buttons */}
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <h5>Payment History</h5>
        {canAddPayment && (
          <Button
            variant="primary"
            onClick={() => setShowPaymentModal(true)}
            disabled={remainingBalance <= 0}
          >
            <FaMoneyBillWave className="me-2" />
            Record Payment
          </Button>
        )}
      </div>

      {/* Payment Status Alert */}
      {remainingBalance <= 0 && totalPaid >= orderTotal && (
        <Alert variant="success">
          <FaCheckCircle className="me-2" />
          This order is fully paid!
        </Alert>
      )}

      {/* Payment List */}
      {payments.length === 0 ? (
        <Alert variant="info">
          No payments recorded yet. Click "Record Payment" to add the first payment.
        </Alert>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Payment #</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Transaction Ref</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment._id}>
                    <td>
                      <strong>{payment.paymentNumber}</strong>
                    </td>
                    <td>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                    <td>
                      <strong className="text-success">
                        ₹{payment.amount.toLocaleString()}
                      </strong>
                    </td>
                    <td>
                      {getPaymentMethodIcon(payment.paymentMethod)}
                      {payment.paymentMethod}
                    </td>
                    <td>{payment.transactionReference || '—'}</td>
                    <td>{getPaymentStatusBadge(payment.status)}</td>
                    <td>{payment.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}


      {/* Record Payment Modal */}
      {canAddPayment && (
        <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Record Payment</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="info">
              <strong>Balance Due:</strong> ₹{remainingBalance.toLocaleString()}
            </Alert>

            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Amount *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="Enter amount"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      max={remainingBalance}
                    />
                    <Form.Text className="text-muted">
                      Maximum: ₹{remainingBalance.toLocaleString()}
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Date *</Form.Label>
                    <Form.Control
                      type="date"
                      value={paymentForm.paymentDate}
                      onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Method *</Form.Label>
                    <Form.Select
                      value={paymentForm.paymentMethod}
                      onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                    >
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cash">Cash</option>
                      <option value="Cheque">Cheque</option>
                      <option value="UPI">UPI</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Debit Card">Debit Card</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Transaction Reference</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="UTR, Cheque No, etc."
                      value={paymentForm.transactionReference}
                      onChange={(e) => setPaymentForm({ ...paymentForm, transactionReference: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Any additional notes..."
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleRecordPayment}>
              Record Payment
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
